import { useState, useRef, useEffect, useMemo } from 'react';
import { trpc } from '../utils/trpc';
import EventStatsModal from '../components/EventStatsModal';
import styles from '../styles/VenueStatsTab.module.css';

// ─── Types ─────────────────────────────────────────────────────

type Period = '30d' | '90d' | '6m' | '1y' | 'all';

interface TagFreq {
  tag: string;
  count: number;
}

interface TopGroup {
  group_id: string;
  group_name: string;
  bookings: number;
  avgAttendees: number;
}

// ─── Period helper ──────────────────────────────────────────────

function periodToStartDate(period: Period): string {
  const now = new Date();
  const map: Record<Period, number> = {
    '30d': 30,
    '90d': 90,
    '6m': 180,
    '1y': 365,
    all: 365 * 10,
  };
  now.setDate(now.getDate() - map[period]);
  return now.toISOString();
}

// ─── Info Tooltip ───────────────────────────────────────────────

function InfoTip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className={styles.tipWrapper} ref={ref}>
      <button
        className={styles.tipBtn}
        onClick={() => setOpen((p) => !p)}
        aria-label="More information"
      >
        ⓘ
      </button>
      {open && (
        <div className={styles.tipPopover} role="tooltip">
          {text}
        </div>
      )}
    </div>
  );
}

// ─── KPI Tile ───────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  sub,
  tip,
  accent,
  delay = 0,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tip: string;
  accent?: 'green' | 'gold' | 'orange';
  delay?: number;
}) {
  return (
    <div
      className={`${styles.kpiTile} ${accent ? styles[`kpiTile_${accent}`] : ''}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={styles.kpiTileHeader}>
        <span className={styles.kpiTileLabel}>{label}</span>
        <InfoTip text={tip} />
      </div>
      <span className={styles.kpiTileValue}>{value}</span>
      {sub && <span className={styles.kpiTileSub}>{sub}</span>}
    </div>
  );
}

// ─── SVG Sparkline ──────────────────────────────────────────────

function Sparkline({
  points,
  color,
  fillColor,
  height = 72,
}: {
  points: number[];
  color: string;
  fillColor: string;
  height?: number;
}) {
  if (points.length < 2) return null;
  const W = 400;
  const H = height;
  const PAD = 6;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  const xs = points.map((_, i) => PAD + (i / (points.length - 1)) * (W - PAD * 2));
  const ys = points.map((v) => H - PAD - ((v - min) / range) * (H - PAD * 2));

  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
  const areaPath = `${linePath} L${xs[xs.length - 1]},${H} L${xs[0]},${H} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={styles.sparkline}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.35" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#grad-${color.replace('#', '')})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r="3.5" fill={color} />
      ))}
    </svg>
  );
}

// ─── Chart Card ─────────────────────────────────────────────────

function ChartCard({
  title,
  tip,
  children,
  delay = 0,
}: {
  title: string;
  tip: string;
  children: React.ReactNode;
  delay?: number;
}) {
  return (
    <div className={styles.chartCard} style={{ animationDelay: `${delay}ms` }}>
      <div className={styles.chartCardHeader}>
        <span className={styles.chartCardTitle}>{title}</span>
        <InfoTip text={tip} />
      </div>
      {children}
    </div>
  );
}

// ─── Tag Bar Chart ───────────────────────────────────────────────

function TagBars({ tags }: { tags: TagFreq[] }) {
  const max = tags[0]?.count ?? 1;
  return (
    <div className={styles.tagBars}>
      {tags.map((t, i) => (
        <div
          key={t.tag}
          className={styles.tagBarRow}
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <span className={styles.tagBarName}>{t.tag}</span>
          <div className={styles.tagBarTrack}>
            <div
              className={styles.tagBarFill}
              style={{ width: `${(t.count / max) * 100}%` }}
            />
          </div>
          <span className={styles.tagBarCount}>{t.count}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Top Groups List ─────────────────────────────────────────────

function TopGroupsList({ groups }: { groups: TopGroup[] }) {
  return (
    <div className={styles.groupList}>
      {groups.map((g, i) => (
        <div
          key={g.group_id}
          className={styles.groupRow}
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <div className={styles.groupRank}>{i + 1}</div>
          <div className={styles.groupInfo}>
            <span className={styles.groupName}>{g.group_name}</span>
            <span className={styles.groupMeta}>
              {g.bookings} bookings · avg {g.avgAttendees} attendees
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Period Selector ─────────────────────────────────────────────

const PERIODS: { key: Period; label: string }[] = [
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: '6m', label: '6m' },
  { key: '1y', label: '1y' },
  { key: 'all', label: 'All' },
];

// ─── Main Component ──────────────────────────────────────────────

interface VenueStatsTabProps {
  venueId: string;
}

export default function VenueStatsTab({ venueId }: VenueStatsTabProps) {
  const [period, setPeriod] = useState<Period>('6m');
  const [statsEvent, setStatsEvent] = useState<{
    event_id: string;
    event_name: string;
  } | null>(null);

  // ── Stable date inputs — only recompute when period changes ──
  const startDate = useMemo(() => periodToStartDate(period), [period]);
  const endDate = useMemo(() => new Date().toISOString(), [period]);

  // ── Real queries ──
  const { data: completedEvents = [], isLoading: eventsLoading } =
    trpc.event.getCompletedEventsByVenueId.useQuery({ venueId });

  const { data: venueStats, isLoading: statsLoading } = trpc.event.getVenueStats.useQuery(
    {
      venueId,
      startDate,
      endDate,
    },
  );

  // ── Derived values with safe fallbacks ──
  const totalEvents = venueStats?.totalEvents ?? 0;
  const avgAttendeeCount = venueStats?.avgAttendeeCount ?? 0;
  const avgAttendanceRate = venueStats?.avgAttendanceRate ?? 0;
  const topTags = venueStats?.topTags ?? [];
  const topGroups = venueStats?.topGroups ?? [];
  const timeline = venueStats?.timeline ?? [];

  return (
    <div className={styles.root}>
      {/* ── Period selector ── */}
      <div className={styles.periodRow}>
        <span className={styles.periodLabel}>Period</span>
        <div className={styles.periodPills}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={`${styles.periodPill} ${period === p.key ? styles.periodPillActive : ''}`}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className={styles.eventCount}>
          {statsLoading ? '—' : `${totalEvents} events`}
        </span>
      </div>

      {/* ── KPI grid ── */}
      <div className={styles.kpiGrid}>
        <KpiTile
          label="Avg Attendees"
          value={statsLoading ? '—' : avgAttendeeCount}
          sub="going + attended per event"
          tip="Average number of attended RSVPs across all completed events in the selected period."
          accent="green"
          delay={0}
        />
        <KpiTile
          label="Avg Attendance Rate"
          value={statsLoading ? '—' : `${avgAttendanceRate}%`}
          sub="attended vs total rsvps"
          tip="Average rate of attended vs total going RSVPs per event. Shows how many who said going actually marked attended."
          accent={
            statsLoading
              ? undefined
              : avgAttendanceRate >= 75
                ? 'green'
                : avgAttendanceRate >= 50
                  ? 'gold'
                  : 'orange'
          }
          delay={60}
        />
        <KpiTile
          label="Avg RSVPs"
          value={statsLoading ? '—' : avgAttendeeCount}
          sub="Average  RSVPs per event"
          tip="Average number of attended RSVPs per event in this period."
          accent="gold"
          delay={120}
        />
        <KpiTile
          label="Avg Revenue"
          value="—"
          sub="ticketing not yet available"
          tip="Total ticket revenue per event. Available once ticketing is enabled."
          delay={180}
        />
      </div>

      {/* ── Charts row ── */}
      <div className={styles.chartsRow}>
        <ChartCard
          title="Attendee Trend"
          tip="Number of going + attended RSVPs per completed event over the selected period, ordered by date."
          delay={220}
        >
          {statsLoading ? (
            <span className={styles.pastEventDate}>Loading...</span>
          ) : timeline.length < 2 ? (
            <span className={styles.pastEventDate}>Not enough data.</span>
          ) : (
            <>
              <div className={styles.chartAxisRow}>
                {timeline.map((p) => (
                  <span key={p.label} className={styles.chartAxisLabel}>
                    {p.label}
                  </span>
                ))}
              </div>
              <Sparkline
                points={timeline.map((p) => p.attendees)}
                color="#4a7a47"
                fillColor="#4a7a47"
              />
            </>
          )}
        </ChartCard>

        <ChartCard
          title="Attended Trend"
          tip="Number of confirmed attended RSVPs per completed event over the selected period."
          delay={280}
        >
          {statsLoading ? (
            <span className={styles.pastEventDate}>Loading...</span>
          ) : timeline.length < 2 ? (
            <span className={styles.pastEventDate}>Not enough data.</span>
          ) : (
            <>
              <div className={styles.chartAxisRow}>
                {timeline.map((p) => (
                  <span key={p.label} className={styles.chartAxisLabel}>
                    {p.label}
                  </span>
                ))}
              </div>
              <Sparkline
                points={timeline.map((p) => p.attended)}
                color="#fcb31b"
                fillColor="#fcb31b"
              />
            </>
          )}
        </ChartCard>
      </div>

      {/* ── Tags + Groups row ── */}
      <div className={styles.insightsRow}>
        <ChartCard
          title="Most Common Interests"
          tip="Interests most frequently associated with completed events at this venue."
          delay={340}
        >
          {statsLoading ? (
            <span className={styles.pastEventDate}>Loading...</span>
          ) : topTags.length === 0 ? (
            <span className={styles.pastEventDate}>No interests yet.</span>
          ) : (
            <TagBars tags={topTags} />
          )}
        </ChartCard>

        <ChartCard
          title="Most Common Groups"
          tip="Groups that have completed the most events at this venue. Sorted by number of bookings."
          delay={400}
        >
          {statsLoading ? (
            <span className={styles.pastEventDate}>Loading...</span>
          ) : topGroups.length === 0 ? (
            <span className={styles.pastEventDate}>No groups yet.</span>
          ) : (
            <TopGroupsList groups={topGroups} />
          )}
        </ChartCard>
      </div>

      {/* ── Past events drill-down — real data ── */}
      <ChartCard
        title="Past Events"
        tip="Individual completed events at this venue. Click 'View Stats' to see per-event statistics."
        delay={460}
      >
        <div className={styles.pastEventsList}>
          {eventsLoading ? (
            <span className={styles.pastEventDate}>Loading...</span>
          ) : completedEvents.length === 0 ? (
            <span className={styles.pastEventDate}>No completed events yet.</span>
          ) : (
            completedEvents.map((ev) => (
              <div key={ev.event_id} className={styles.pastEventRow}>
                <div className={styles.pastEventInfo}>
                  <span className={styles.pastEventName}>{ev.event_name}</span>
                  <span className={styles.pastEventDate}>
                    {new Date(ev.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <button
                  className={styles.btnViewStats}
                  onClick={() =>
                    setStatsEvent({ event_id: ev.event_id, event_name: ev.event_name })
                  }
                >
                  View Stats
                </button>
              </div>
            ))
          )}
        </div>
      </ChartCard>

      {/* ── Event stats modal ── */}
      {statsEvent && (
        <EventStatsModal
          eventId={statsEvent.event_id}
          eventName={statsEvent.event_name}
          onClose={() => setStatsEvent(null)}
        />
      )}
    </div>
  );
}
